import React from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Line,
} from 'recharts';
import { Waveform, WarningCircle } from '@phosphor-icons/react';
import InfoHint from '../common/InfoHint';

/**
 * Seasonal saturation curve (Overview / As-Is, Part 0 -> Tri-Node).
 *
 * Reads the design days already computed by the pipeline
 * (analysis_results.parts.part0_archaeology.design_days) and, for each month's
 * busy day, compares its peak-hour movements against the Tri-Node controlling
 * capacity (part3.controlling_capacity). The saturation metric is the canonical
 * SVS, replicated VERBATIM from the backend (equilibrium_logic.run_equilibrium_analysis):
 *
 *     SVS = 1 - max(0, demand - CC) / demand      (demand > 0)
 *     SVS = 1.0                                    (demand <= 0)
 *
 * so SVS = 1 means the month's peak demand fits under CC and (1 - SVS) is the
 * fraction of peak demand that exceeds capacity.
 *
 * HONESTY (glass box): the controlling capacity is held CONSTANT across the year
 * — it comes from the single As-Is busy-day run; the pipeline does NOT recompute
 * RAC/OIF/CF per month. This curve varies ONLY demand. It is a demand-side
 * seasonal read, not a per-month re-analysis, and it is labelled as such. When
 * the CC itself is estimated (data_confidence != 'high'), the curve inherits
 * that uncertainty and says so. No value here is fabricated.
 */

type DesignDay = {
  date: string;
  total_movements: number;
  peak_hour: number;
  peak_hour_movements: number;
  year?: number;
  month?: number;
  month_name?: string;
  // Seasonal capacity attached by seasonal_capacity.py (present after re-analysis).
  cc?: number;
  provenance?: any;
};

export interface SeasonalSaturationCurveProps {
  project: any;                 // analysis_results.parts.part0_archaeology + provenance
  part3: any;                   // controlling_capacity, limiting_nodes
  language: 'es' | 'en';
}

/** SVS, verbatim from equilibrium_logic.py:54-57. Returns null if CC unknown. */
function svsOf(demand: number, cc: number): number | null {
  if (cc <= 0) return null;
  if (demand <= 0) return 1.0;
  const excess = Math.max(0, demand - cc);
  return 1.0 - excess / demand;
}

const MONTHS_SHORT: Record<'es' | 'en', string[]> = {
  es: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

// SVS bands: 1.0 = peak demand at/under CC; < 1 means demand exceeds CC.
// SVS 0.90 corresponds to peak demand ~11% over capacity.
function bandToken(svs: number | null): string {
  if (svs === null) return 'var(--text-tertiary)';
  if (svs >= 1.0) return 'var(--aipron-success)';
  if (svs >= 0.9) return 'var(--aipron-warning)';
  return 'var(--aipron-danger)';
}

const SeasonalSaturationCurve: React.FC<SeasonalSaturationCurveProps> = ({ project, part3, language }) => {
  const es = language === 'es';

  const designDays = project?.analysis_results?.parts?.part0_archaeology?.design_days;
  const busyDays: DesignDay[] = Array.isArray(designDays?.busy_day_by_month)
    ? designDays.busy_day_by_month
    : [];
  const lowDay: DesignDay | null = designDays?.low_day ?? null;

  // Nothing to draw without design days (DataArchaeologyPanel already explains why).
  if (!designDays?.available || busyDays.length === 0) return null;

  const cc: number = typeof part3?.controlling_capacity === 'number' ? part3.controlling_capacity : 0;
  const confidence: string | undefined = project?.analysis_results?.provenance?.data_confidence;
  const ccEstimated = !!confidence && confidence !== 'high';
  // Seasonal = at least one month carries its own recomputed CC (seasonal_capacity.py).
  // Otherwise every month falls back to the constant annual CC (old data / not re-run).
  const seasonal = busyDays.some((d) => typeof d.cc === 'number' && (d.cc as number) > 0);

  const monthLabel = (d: DesignDay) => {
    const m = typeof d.month === 'number' ? d.month : 0;
    const short = m >= 1 && m <= 12 ? MONTHS_SHORT[es ? 'es' : 'en'][m - 1] : (d.month_name || '');
    return d.year ? `${short} ${String(d.year).slice(2)}` : short;
  };

  const rows = busyDays.map((d) => {
    const demand = d.peak_hour_movements;
    // Per-month CC when available, else the constant annual CC (fallback).
    const ccMonth = typeof d.cc === 'number' && d.cc > 0 ? d.cc : cc;
    const svs = svsOf(demand, ccMonth);
    return {
      label: monthLabel(d),
      demand,
      ccMonth: ccMonth > 0 ? ccMonth : null,
      svs,
      svsPct: svs === null ? null : Math.round(svs * 1000) / 10,
      repressedPct: svs === null ? null : Math.max(0, Math.round((1 - svs) * 1000) / 10),
      date: d.date,
      peakHour: d.peak_hour,
      token: bandToken(svs),
    };
  });
  const ccMin = Math.min(...rows.map((r) => r.ccMonth ?? Infinity).filter((v) => Number.isFinite(v)));
  const ccMax = Math.max(...rows.map((r) => r.ccMonth ?? 0));

  const saturatedMonths = rows.filter((r) => r.svs !== null && r.svs < 1).length;
  const worst = rows.reduce<typeof rows[number] | null>(
    (acc, r) => (r.svs !== null && (acc === null || (acc.svs ?? 1) > r.svs) ? r : acc),
    null,
  );
  const singleMonth = busyDays.length < 2;

  const axis = 'var(--text-tertiary)';
  const grid = 'var(--border-subtle)';

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const r = payload[0].payload as typeof rows[number];
    return (
      <div className="rounded-lg border p-3 text-xs" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}>
        <p className="font-bold font-mono mb-1">{r.label} · {r.date}</p>
        <p>{es ? 'Demanda pico' : 'Peak demand'}: <span className="font-mono font-bold">{Math.round(r.demand)}</span> mov/h @ {r.peakHour}h</p>
        {r.ccMonth && <p>{es ? 'Capacidad (CC)' : 'Capacity (CC)'}: <span className="font-mono">{Math.round(r.ccMonth)}</span> mov/h{seasonal ? (es ? ' (mes)' : ' (month)') : ''}</p>}
        {r.svsPct !== null ? (
          <p style={{ color: r.token }} className="font-bold mt-1">
            SVS {r.svsPct}%{r.repressedPct && r.repressedPct > 0 ? ` · ${es ? 'reprimida' : 'unmet'} ${r.repressedPct}%` : ''}
          </p>
        ) : (
          <p className="text-[var(--text-tertiary)] mt-1">{es ? 'Sin capacidad controladora' : 'No controlling capacity'}</p>
        )}
      </div>
    );
  };

  return (
    <div className="pro-card p-6">
      <div className="flex flex-wrap justify-between items-start mb-1 gap-3">
        <div>
          <h3 className="font-bold text-lg font-display tracking-tight text-[var(--text-primary)] flex items-center gap-2">
            <Waveform size={18} weight="fill" className="text-[var(--aipron-accent)]" />
            {es ? 'Curva de saturación estacional' : 'Seasonal saturation curve'}
            <InfoHint
              align="left"
              text={es
                ? `Compara, mes a mes, la demanda punta del día representativo (busy day de cada mes) contra la capacidad controladora (CC). El SVS resume cuánta demanda cabe: verde = por debajo de la CC; ámbar/rojo = la punta supera la capacidad. ${seasonal ? 'La CC se recalcula por mes (línea) sobre los factores sensibles al tráfico de cada mes; ver el detalle de qué se recalcula y qué se mantiene anual en la provenance.' : 'La CC se mantiene constante (análisis As-Is), así que aquí solo varía la demanda.'}`
                : `Compares, month by month, the peak demand of the representative day (each month's busy day) against the controlling capacity (CC). SVS summarises how much demand fits: green = below CC; amber/red = the peak exceeds capacity. ${seasonal ? 'CC is recomputed per month (the line) from each month\'s traffic-sensitive factors; see the provenance for what is recomputed vs held annual.' : 'CC is held constant (As-Is analysis), so only demand varies here.'}`}
            />
          </h3>
          <p className="text-xs mt-1 text-[var(--text-secondary)]">
            {es
              ? 'Demanda pico del busy-day de cada mes vs capacidad controladora del tri-nodo (SVS)'
              : 'Each month busy-day peak demand vs Tri-Node controlling capacity (SVS)'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <LegendDot token="var(--aipron-success)" label={es ? 'Bajo capacidad' : 'Under capacity'} />
          <LegendDot token="var(--aipron-warning)" label={es ? 'Al límite' : 'At limit'} />
          <LegendDot token="var(--aipron-danger)" label={es ? 'Saturado' : 'Saturated'} />
        </div>
      </div>

      {/* Headline summary — honest even when CC is unknown */}
      {cc > 0 ? (
        <p className="text-xs mb-4 text-[var(--text-secondary)]">
          {(() => {
            const capLabel = seasonal
              ? `${Number.isFinite(ccMin) ? Math.round(ccMin) : '—'}–${ccMax > 0 ? Math.round(ccMax) : '—'} mov/h${es ? ', estacional' : ', seasonal'}`
              : `${Math.round(cc)} mov/h`;
            return saturatedMonths === 0
              ? (es ? `Ningún mes supera la capacidad controladora (${capLabel}).` : `No month exceeds the controlling capacity (${capLabel}).`)
              : (es ? `${saturatedMonths} ${saturatedMonths === 1 ? 'mes supera' : 'meses superan'} la capacidad (${capLabel})` : `${saturatedMonths} ${saturatedMonths === 1 ? 'month exceeds' : 'months exceed'} capacity (${capLabel})`);
          })()}
          {worst && worst.svs !== null && worst.svs < 1 && (
            <span> — {es ? 'peor' : 'worst'}: <span className="font-mono font-bold" style={{ color: worst.token }}>{worst.label} (SVS {worst.svsPct}%)</span></span>
          )}
        </p>
      ) : (
        <p className="text-xs mb-4 text-[var(--aipron-warning)] flex items-center gap-1.5">
          <WarningCircle size={14} weight="fill" />
          {es
            ? 'Sin capacidad controladora configurada — se muestra la demanda pico, pero no se puede evaluar la saturación.'
            : 'No controlling capacity configured — peak demand is shown, but saturation cannot be evaluated.'}
        </p>
      )}

      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={grid} />
            <XAxis dataKey="label" stroke={axis} fontSize={11} tickLine={false} axisLine={false} interval={0} angle={rows.length > 8 ? -45 : 0} textAnchor={rows.length > 8 ? 'end' : 'middle'} height={rows.length > 8 ? 54 : 30} />
            <YAxis stroke={axis} fontSize={11} tickLine={false} axisLine={false} label={{ value: 'mov/h', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: axis } }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'color-mix(in srgb, var(--aipron-accent) 8%, transparent)' }} />
            <Bar dataKey="demand" radius={[3, 3, 0, 0]} maxBarSize={48}>
              {rows.map((r, i) => (
                <Cell key={i} fill={r.token} fillOpacity={0.85} />
              ))}
            </Bar>
            {/* Capacity: a per-month line when seasonal CC is available, otherwise a
                single constant reference line (old data / not re-analysed). */}
            {seasonal ? (
              <Line
                type="monotone"
                dataKey="ccMonth"
                stroke="var(--aipron-accent)"
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={{ r: 2, fill: 'var(--aipron-accent)' }}
                isAnimationActive={false}
                connectNulls
              />
            ) : cc > 0 ? (
              <ReferenceLine
                y={cc}
                stroke="var(--aipron-accent)"
                strokeWidth={2}
                strokeDasharray="6 3"
                label={{ value: `CC ${Math.round(cc)}`, position: 'insideTopRight', fill: 'var(--aipron-accent)', fontSize: 10, fontWeight: 700 }}
              />
            ) : null}
            {lowDay && typeof lowDay.peak_hour_movements === 'number' && (
              <ReferenceLine
                y={lowDay.peak_hour_movements}
                stroke={axis}
                strokeWidth={1}
                strokeDasharray="3 3"
                label={{ value: es ? 'valle' : 'low', position: 'insideBottomRight', fill: axis, fontSize: 9 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Glass box: what this curve is and is not */}
      <div className="mt-4 pt-3 border-t border-[var(--border-subtle)] space-y-1">
        <p className="text-xs text-[var(--text-tertiary)]">
          {seasonal
            ? (es
                ? `Demanda pico mensual vs capacidad controladora recalculada por mes (${Number.isFinite(ccMin) ? Math.round(ccMin) : '—'}–${ccMax > 0 ? Math.round(ccMax) : '—'} mov/h). Se recalculan los factores sensibles al tráfico del mes (p.ej. operación nocturna, demanda pico y, si hay config de pista, el mix RECAT); los estructurales (meteorología, mix→AM, terminal) se mantienen anuales — cada mes trae su provenance. SVS = 1 − (demanda − CC)/demanda.`
                : `Monthly peak demand vs a per-month recomputed controlling capacity (${Number.isFinite(ccMin) ? Math.round(ccMin) : '—'}–${ccMax > 0 ? Math.round(ccMax) : '—'} mov/h). The month's traffic-sensitive factors are recomputed (e.g. night ops, peak demand, and the RECAT mix when a runway config exists); structural ones (weather, mix->AM, terminal) are held annual — each month carries its provenance. SVS = 1 − (demand − CC)/demand.`)
            : (es
                ? 'Demanda pico mensual (busy-day canónico de cada mes) contra la capacidad controladora As-Is, que se mantiene constante: no es un re-análisis por mes — la capacidad no se recalcula por estacionalidad, solo varía la demanda. Re-ejecuta el análisis para obtener la CC estacional. SVS = 1 − (demanda − CC)/demanda.'
                : 'Monthly peak demand (each month canonical busy day) against the As-Is controlling capacity, held constant: this is not a per-month re-analysis — capacity is not recomputed for seasonality, only demand varies. Re-run the analysis to get the seasonal CC. SVS = 1 − (demand − CC)/demand.')}
        </p>
        {ccEstimated && (
          <p className="text-xs text-[var(--aipron-warning)]">
            {es
              ? `La capacidad controladora es una estimación (confianza: ${confidence}) — la curva hereda esa incertidumbre.`
              : `The controlling capacity is estimated (confidence: ${confidence}) — the curve inherits that uncertainty.`}
          </p>
        )}
        {singleMonth && (
          <p className="text-xs text-[var(--text-tertiary)]">
            {es
              ? 'Datos de un solo mes: no hay estacionalidad observable todavía.'
              : 'Single month of data: no observable seasonality yet.'}
          </p>
        )}
      </div>
    </div>
  );
};

const LegendDot: React.FC<{ token: string; label: string }> = ({ token, label }) => (
  <div className="flex items-center gap-1.5 text-xs font-bold uppercase">
    <div className="w-3 h-3 rounded-full" style={{ background: token }}></div>
    <span className="text-[var(--text-primary)]">{label}</span>
  </div>
);

export default SeasonalSaturationCurve;
